import { useEffect, useRef, useState } from "react";
import { Camera, RefreshCw, X as XIcon } from "lucide-react";

// ═══════════════════════════════════════════════════════════════
// CÂMERA DENTRO DO SITE
// ═══════════════════════════════════════════════════════════════
//
// O botão de arquivo com `capture` abre o app de câmera do Android. Enquanto
// ele está aberto, o sistema fecha o navegador para liberar memória (comum em
// tablet), e ao confirmar a foto a aba recarrega do zero: some a foto e tudo o
// que foi preenchido. Aqui a câmera roda na própria página (getUserMedia) —
// não há troca de app, então não há o que o Android fechar.

export const cameraNoSiteDisponivel = () =>
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;

// `lado`: maior lado da foto final. Documento pede mais (texto legível).
export default function CameraCaptura({ aberta, onFechar, onFoto, lado = 1600, prefixo = "visita" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [frontal, setFrontal] = useState(false);
  const [erro, setErro] = useState("");
  const [pronta, setPronta] = useState(false);

  useEffect(() => {
    if (!aberta) return undefined;
    let cancelado = false;
    setErro(""); setPronta(false);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: frontal ? "user" : { ideal: "environment" }, width: { ideal: lado > 1920 ? 3840 : 1920 }, height: { ideal: lado > 1920 ? 2160 : 1080 } },
          audio: false,
        });
        if (cancelado) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setPronta(true);
      } catch (e) {
        setErro(e?.name === "NotAllowedError"
          ? "O navegador bloqueou a câmera. Toque no cadeado ao lado do endereço do site, permita a Câmera e tente de novo."
          : "Não consegui abrir a câmera neste aparelho. Use \"Escolher da galeria\".");
      }
    })();
    return () => {
      cancelado = true;
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [aberta, frontal]);

  if (!aberta) return null;

  const capturar = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const escala = Math.min(1, lado / Math.max(v.videoWidth, v.videoHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(v.videoWidth * escala);
    c.height = Math.round(v.videoHeight * escala);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    c.toBlob((blob) => {
      if (!blob) { setErro("Não consegui gerar a foto. Tente de novo."); return; }
      const nome = `${prefixo}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.jpg`;
      onFoto(new File([blob], nome, { type: "image/jpeg" }));
      onFechar();
    }, "image/jpeg", lado > 1920 ? 0.9 : 0.85);
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: "#000", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        <video ref={videoRef} playsInline muted autoPlay style={{ width: "100%", height: "100%", objectFit: "contain", transform: frontal ? "scaleX(-1)" : "none" }} />
        {!pronta && !erro && <div style={{ position: "absolute", color: "#fff", fontSize: 14 }}>Abrindo a câmera...</div>}
        {erro && (
          <div style={{ position: "absolute", left: 20, right: 20, padding: 16, borderRadius: 10, background: "#fff", color: "#b91c1c", fontSize: 13, lineHeight: 1.5, textAlign: "center" }}>{erro}</div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "18px 24px calc(18px + env(safe-area-inset-bottom))", background: "#111" }}>
        <button type="button" onClick={onFechar} style={btnLado}><XIcon size={18} /> Cancelar</button>
        <button type="button" onClick={capturar} disabled={!pronta} aria-label="Tirar foto"
          style={{ width: 72, height: 72, borderRadius: "50%", border: "4px solid #fff", background: pronta ? "#d97706" : "#555", display: "flex", alignItems: "center", justifyContent: "center", cursor: pronta ? "pointer" : "default" }}>
          <Camera size={28} color="#fff" />
        </button>
        <button type="button" onClick={() => setFrontal((f) => !f)} style={btnLado}><RefreshCw size={18} /> Virar</button>
      </div>
    </div>
  );
}

const btnLado = {
  display: "flex", alignItems: "center", gap: 6, padding: "10px 14px", borderRadius: 8,
  border: "1px solid #444", background: "transparent", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", minWidth: 100, justifyContent: "center",
};
